'use client';

import { Input } from '@ui/components/ui/input';
import { MultiSelect } from '@ui/components/ui/multi-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/components/ui/select';
import { useFilters } from '@/contexts/app-context';
import { Tag } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useSidebar } from '@ui/components/ui/sidebar';

export default function Filters() {
  const t = useTranslations('Filter');
  const { isMobile } = useSidebar();
  const { filters, updateFilter, filterOptions, hasFilterOptions } = useFilters();

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateFilter('search', event.target.value);
  };

  const handleTagsChange = (selectedTags: string[]) => {
    updateFilter('tags', selectedTags);
  };

  const handleSelectChange = (filterKey: string, value: string) => {
    updateFilter(filterKey, value);
  };

  // Show filters if there are filter options available
  if (!hasFilterOptions) {
    return null;
  }

  return (
    <div className="col-span-5 lg:col-span-4 flex justify-center gap-3 items-center w-full">
      {/* Search Input */}
      {filterOptions.search && (
        <Input type="text" placeholder={t('search.placeholder')} value={filters.search || ''} onChange={handleSearchChange} className="max-w-md" />
      )}

      {/* Tags Multi-Select */}
      {filterOptions.tags && filterOptions.tags.length > 0 && (
        <MultiSelect
          options={filterOptions.tags.map((tag) => ({
            label: tag.label,
            value: tag.value,
            icon: Tag,
          }))}
          onValueChange={handleTagsChange}
          defaultValue={filters.tags || []}
          placeholder={t('tags.placeholder')}
          maxCount={isMobile ? 0 : 1}
          className="w-44 md:min-w-sm xl:min-w-md"
        />
      )}

      {/* Generic Select Filters */}
      {Object.entries(filterOptions).map(([key, value]) => {
        // Skip special keys that are already handled
        if (key === 'search' || key === 'tags') return null;

        // Only render if value is an array (select options)
        if (!Array.isArray(value)) return null;

        const currentValue = (filters[key] as string) || '';

        return (
          <div key={key} className="w-48">
            <Select value={currentValue} onValueChange={(val) => handleSelectChange(key, val)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {value.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      })}
    </div>
  );
}
